import api from './api';

/**
 * GP-370 — Patient360 Documents tab.
 *
 * Two document sources, one list:
 *  - PHOX-uploaded rows from `patient_documents` (GCS-backed, editable, pinnable)
 *  - Kroll-side documents listed live over MQTT (view-only; Kroll remains the
 *    storage until a row is explicitly imported)
 *
 * All endpoints live under /api/patients/:patientId/documents — the JWT rides
 * the shared axios instance, so binary previews are fetched as blobs (an
 * <iframe src> would not carry the Authorization header).
 */

export type PhoxDocumentType =
  | 'medical_clearance'
  | 'prescription_image'
  | 'insurance_card'
  | 'general'
  | 'billing'
  | 'delivery'
  | 'clinical'
  // WP-10 (PRX-130): the signature form and the proof of travel kept with it.
  | 'vacation_supply_form'
  | 'proof_of_travel';

export interface PhoxPatientDocument {
  id: string;
  pharmacyId: string;
  patientCompositeId: string;
  documentType: PhoxDocumentType;
  status: 'active' | 'archived' | 'expired';
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  title?: string;
  description?: string;
  uploadedBy: string;
  source: 'phox_upload' | 'kroll_import';
  pinned: boolean;
  krollPatDocumentId?: number;
  /** WP-10: the Rx numbers the document covers. Present only when the list was read with `withRxNumbers`. */
  rxNumbers?: string[] | null;
  createdAt: string;
}

export interface KrollPatientDocument {
  krollId: number;
  title: string;
  description: string | null;
  krollDocumentType: number;
  krollDocumentTypeLabel: string;
  reportNumber: number | null;
  uploadedByInit: string | null;
  mimeType: string | null;
  contentBytes: number | null;
  tooLargeForPreview: boolean;
  dateCreated: string;
  dateChanged: string | null;
  alreadyImported: boolean;
  importedDocumentId: string | null;
  suggestedCategory: PhoxDocumentType;
}

export async function listPhoxDocuments(
  patientId: string,
  // WP-10: only a Fillware screen asks for the Rx numbers, so no other read
  // names the `rx_numbers` column (it may not exist before its migration runs).
  options?: { withRxNumbers?: boolean },
): Promise<PhoxPatientDocument[]> {
  const response = await api.get(
    `/api/patients/${patientId}/documents`,
    options?.withRxNumbers ? { params: { withRxNumbers: 'true' } } : undefined,
  );
  if (!response.data?.success) {
    throw new Error('Failed to load patient documents');
  }
  return response.data.data as PhoxPatientDocument[];
}

export interface KrollDocumentList {
  rows: KrollPatientDocument[];
  /** Total PatDocument rows in Kroll for this patient (may exceed rows.length) */
  totalCount: number;
  /** True when older rows were omitted by the server-side window */
  truncated: boolean;
}

export async function listKrollDocuments(
  patientId: string,
): Promise<KrollDocumentList> {
  const response = await api.get(`/api/patients/${patientId}/documents/kroll`);
  if (!response.data?.success) {
    throw new Error('Failed to load Kroll documents');
  }
  return {
    rows: response.data.data as KrollPatientDocument[],
    totalCount: Number(response.data.totalCount ?? response.data.count ?? 0),
    truncated: Boolean(response.data.truncated),
  };
}

/**
 * Kroll document binary as an object URL (caller must revoke it).
 *
 * `convertError` is set when the backend could not turn a TIFF into a
 * browser-renderable PNG. The bytes are still the real document, but no
 * browser will display them, so the caller must say so rather than opening a
 * blank tab.
 *
 * Pass `signal` to abandon the fetch — a Kroll blob takes 1.5–2.7s over MQTT,
 * and a viewer the user already closed should not keep it running (GP-1383).
 */
export async function fetchKrollDocumentBlobUrl(
  patientId: string,
  krollId: number,
  options?: { signal?: AbortSignal },
): Promise<{ url: string; mimeType: string; convertError?: string }> {
  const response = await api.get(
    `/api/patients/${patientId}/documents/kroll/${krollId}/content`,
    { responseType: 'blob', signal: options?.signal },
  );
  const blob = response.data as Blob;
  const convertError = response.headers?.['x-kroll-doc-convert-error'];
  return {
    url: URL.createObjectURL(blob),
    mimeType: blob.type,
    ...(convertError ? { convertError: String(convertError) } : {}),
  };
}

/** PHOX document: signed GCS URL (opens directly, no auth header needed). */
export async function fetchPhoxDocumentDownloadUrl(
  patientId: string,
  docId: string,
  options?: { signal?: AbortSignal },
): Promise<{ url: string; fileName: string }> {
  const response = await api.get(
    `/api/patients/${patientId}/documents/${docId}/download`,
    { signal: options?.signal },
  );
  if (!response.data?.success) {
    throw new Error('Failed to get document download URL');
  }
  return response.data.data as { url: string; fileName: string };
}

export async function importKrollDocument(
  patientId: string,
  krollId: number,
  documentType?: PhoxDocumentType,
): Promise<{ document: PhoxPatientDocument; alreadyImported: boolean }> {
  const response = await api.post(
    `/api/patients/${patientId}/documents/kroll/${krollId}/import`,
    documentType ? { documentType } : {},
  );
  if (!response.data?.success) {
    throw new Error('Failed to import Kroll document');
  }
  return {
    document: response.data.data as PhoxPatientDocument,
    alreadyImported: Boolean(response.data.alreadyImported),
  };
}

/**
 * Soft-delete a PHOX document (GP-1383: backs the "Undo" on an import).
 *
 * The backend soft-deletes, and the Kroll import dedupe only matches rows with
 * `deletedAt IS NULL` — so an undone import cleanly re-offers the Kroll row and
 * can be imported again.
 */
export async function deletePhoxDocument(
  patientId: string,
  docId: string,
): Promise<void> {
  const response = await api.delete(
    `/api/patients/${patientId}/documents/${docId}`,
  );
  if (!response.data?.success) {
    throw new Error('Failed to delete document');
  }
}

export async function updatePhoxDocument(
  patientId: string,
  docId: string,
  patch: {
    pinned?: boolean;
    documentType?: PhoxDocumentType;
    title?: string;
    description?: string;
  },
): Promise<PhoxPatientDocument> {
  const response = await api.patch(
    `/api/patients/${patientId}/documents/${docId}`,
    patch,
  );
  if (!response.data?.success) {
    throw new Error('Failed to update document');
  }
  return response.data.data as PhoxPatientDocument;
}

export const PHOX_DOCUMENT_TYPE_LABELS: Record<PhoxDocumentType, string> = {
  billing: 'Billing',
  delivery: 'Delivery',
  clinical: 'Clinical',
  general: 'General',
  medical_clearance: 'Medical Clearance',
  prescription_image: 'Prescription Image',
  insurance_card: 'Insurance Card',
  vacation_supply_form: 'Vacation supply form',
  proof_of_travel: 'Proof of travel',
};

/**
 * WP-10 (PRX-130 Q15): what the vacation supply form prints. Dates are
 * YYYY-MM-DD. The patient is not sent: the server reads the name from the PHOX
 * record at the path pharmacy.
 */
export interface VacationSupplyFormRequest {
  departureDate: string;
  returnDate: string;
  destination: 'in_ontario' | 'outside_ontario';
  bufferDays: number;
  fillDate?: string;
  items: Array<{
    rxNumber: string;
    drugName: string;
    fillQty: number;
    fillDays: number;
    bridgeQty: number;
    bridgeDays: number;
  }>;
}

/**
 * WP-10 — generate the vacation supply form for the patient's signature and
 * save it to the patient's documents, tied to its Rx numbers. Fillware
 * pharmacies only; the server refuses any other. `alreadyExisted` is true when
 * the identical form (every printed word) was already saved today: that form
 * comes back and no second one is made. `earlierFormToday` is true when a new
 * form was made while a different one for the same travel dates exists today.
 */
export async function createVacationSupplyForm(
  pharmacyId: string,
  patientId: string,
  request: VacationSupplyFormRequest,
): Promise<{ document: PhoxPatientDocument; alreadyExisted: boolean; earlierFormToday: boolean }> {
  const response = await api.post(
    `/api/patient-documents/pharmacies/${pharmacyId}/patients/${patientId}/vacation-supply-form`,
    request,
  );
  if (!response.data?.success) {
    throw new Error('Failed to create the vacation supply form');
  }
  return {
    document: response.data.data as PhoxPatientDocument,
    alreadyExisted: response.data.alreadyExisted === true,
    earlierFormToday: response.data.earlierFormToday === true,
  };
}

/** The file types and size the Documents tab upload accepts (`PatientDocumentUpload`). */
export const PATIENT_DOCUMENT_ACCEPT = 'image/jpeg,image/png,image/gif,application/pdf';
export const PATIENT_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

/** Why a file can't be uploaded as a patient document, or null when it can. */
export function patientDocumentFileProblem(file: File): string | null {
  if (!PATIENT_DOCUMENT_ACCEPT.split(',').includes(file.type)) return 'Choose a PDF, JPEG, PNG or GIF file';
  if (file.size > PATIENT_DOCUMENT_MAX_BYTES) return 'The file is larger than 25 MB';
  return null;
}

/** Upload one file to the patient's documents: the Documents tab's own endpoint and multipart shape. */
export async function uploadPatientDocument(params: {
  patientId: string;
  pharmacyId: string;
  file: File;
  documentType: PhoxDocumentType;
  uploadedBy: string;
  title: string;
}): Promise<PhoxPatientDocument> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('documentType', params.documentType);
  formData.append('pharmacyId', params.pharmacyId);
  formData.append('uploadedBy', params.uploadedBy);
  formData.append('title', params.title);
  const response = await api.post(`/api/patients/${params.patientId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data?.success) {
    throw new Error('Failed to upload the document');
  }
  return response.data.data as PhoxPatientDocument;
}
